/** Original synthesized arrangement: no audio file, download, third-party call, or autoplay bypass. */
export const BIRTHDAY_NOTES:readonly (readonly [number,number])[] = [
  [67,.5],[67,.5],[69,1],[67,1],[72,1],[71,2],
  [67,.5],[67,.5],[69,1],[67,1],[74,1],[72,2],
  [67,.5],[67,.5],[79,1],[76,1],[72,1],[71,1],[69,2],
  [77,.5],[77,.5],[76,1],[72,1],[74,1],[72,2],
];
const BEAT_SECONDS=.40;
export const BIRTHDAY_AUDIO_SECONDS=BIRTHDAY_NOTES.reduce((sum,n)=>sum+n[1]*BEAT_SECONDS,0);
export type AudioStatus='muted'|'ready'|'needs-gesture'|'unavailable';

export class PartyAudio {
  private context:AudioContext|null=null;
  private master:GainNode|null=null;
  private muted=true;
  private unavailable=false;
  private voices=new Set<OscillatorNode>();
  private cues=new Map<string,number>();
  private lastPreview=0;

  get status():AudioStatus {
    return this.unavailable?'unavailable':this.muted?'muted':this.context?.state==='running'?'ready':'needs-gesture';
  }
  /** Call directly inside click/submit, before awaiting any network operation. */
  async setMuted(value:boolean):Promise<AudioStatus> {
    this.muted=value;
    if(value){
      this.stopVoices();
      if(this.context&&this.master){this.master.gain.cancelScheduledValues(this.context.currentTime);this.master.gain.setValueAtTime(0,this.context.currentTime);}
      return this.status;
    }
    try {
      this.context??=new AudioContext({latencyHint:'interactive'});
      if(!this.master){this.master=this.context.createGain();this.master.gain.value=.22;this.master.connect(this.context.destination);}
      this.master.gain.setValueAtTime(.22,this.context.currentTime);
      await this.context.resume();
    } catch {this.unavailable=true;}
    return this.status;
  }
  private tone(midi:number,start:number,duration:number,type:OscillatorType,volume:number){
    if(!this.context||!this.master||this.voices.size>=96)return;
    const oscillator=this.context.createOscillator(),gain=this.context.createGain();
    oscillator.type=type;oscillator.frequency.setValueAtTime(440*Math.pow(2,(midi-69)/12),start);
    gain.gain.setValueAtTime(0,start);gain.gain.linearRampToValueAtTime(volume,start+.006);
    gain.gain.setValueAtTime(volume,start+Math.max(.01,duration-.035));gain.gain.linearRampToValueAtTime(0,start+duration);
    oscillator.connect(gain);gain.connect(this.master);this.voices.add(oscillator);
    oscillator.onended=()=>{this.voices.delete(oscillator);oscillator.disconnect();gain.disconnect();};
    oscillator.start(start);oscillator.stop(start+duration+.012);
  }
  private claim(id:string,now:number):boolean {
    if(this.status!=='ready'||this.cues.has(id))return false;
    this.cues.set(id,now);
    // Multiple simultaneous effects must not alternate and replay every animation frame.
    for(const [key,time]of this.cues)if(now-time>120000)this.cues.delete(key);
    while(this.cues.size>256)this.cues.delete(this.cues.keys().next().value!);
    return true;
  }
  birthday(id:string,startedAt:number,now:number):void {
    const elapsed=(now-startedAt)/1000;
    if(elapsed<0||elapsed>=BIRTHDAY_AUDIO_SECONDS||!this.claim(`birthday:${id}`,now)||!this.context)return;
    // Late arrivals and users enabling sound mid-song hear the correct remaining phrase.
    const base=this.context.currentTime+.035;let offset=0;
    for(const [midi,beats]of BIRTHDAY_NOTES){
      const length=beats*BEAT_SECONDS,end=offset+length;
      if(end>elapsed){const remaining=end-Math.max(offset,elapsed);const at=base+Math.max(0,offset-elapsed);
        this.tone(midi,at,Math.max(.025,remaining*.9),'square',.19);
        this.tone(midi-24,at,Math.max(.025,remaining*.65),'triangle',.16);
      }
      offset=end;
    }
  }
  fireworks(id:string,startedAt:number,now:number):void {
    if(now<startedAt||now-startedAt>1600||!this.claim(`fireworks:${id}`,now)||!this.context)return;
    const start=this.context.currentTime+.015;
    for(let i=0;i<4;i++)this.tone(79+i*3,start+i*.07,.12,'triangle',.13);
  }
  async preview():Promise<AudioStatus> {
    const status=await this.setMuted(false),now=Date.now();
    if(now-this.lastPreview<1000)return status;
    this.lastPreview=now;this.stopVoices();this.birthday(`preview-${now}`,now,now);
    return status;
  }
  private stopVoices(){for(const voice of this.voices){try{voice.stop();}catch{/* Already ended. */}voice.disconnect();}this.voices.clear();}
  dispose(){this.stopVoices();this.cues.clear();if(this.context)void this.context.close();this.context=null;this.master=null;}
}
